export type Language = "en" | "ar" | "ru";

export const languageNames: Record<Language, string> = {
  en: "English",
  ar: "العربية",
  ru: "Русский",
};

export const translations: Record<Language, Record<string, string>> = {
  en: {
    // Hero
    "hero.badge": "AI-Powered Investment Intelligence",
    "hero.title1": "Get Your First",
    "hero.title2": "AI Stock Pick Free",
    "hero.subtitle":
      "Tell us your budget and goals. Get AI stock picks with projected ROI in 60 seconds - free, no credit card needed.",
    "hero.cta": "Get Your Free Stock Pick",
    "hero.viewPricing": "View Pricing",
    "hero.powered": "Powered by Advanced AI Models",
    "hero.smartPicks": "Smart Picks",
    "hero.riskAnalysis": "Risk Analysis",
    "hero.roiForecast": "ROI Forecast",
    "hero.howItWorksLabel": "How It Works",
    "hero.step1": "Enter your budget and goals",
    "hero.step2": "AI analyzes the market in real time",
    "hero.step3": "Get personalized stock picks with ROI",

    // Pricing
    "pricing.title1": "Choose Your",
    "pricing.title2": "Edge",
    "pricing.subtitle": "Choose monthly flexibility or save big with yearly plans. Cancel anytime.",
    "pricing.mostPopular": "MOST POPULAR",
    "pricing.monthly": "Monthly",
    "pricing.yearly": "Yearly",
    "pricing.save": "SAVE",
    "pricing.month": "mo",
    "pricing.year": "yr",
    "pricing.savePercent": "Save {percent}% with yearly",
    "pricing.runsPerMonth": "runs/month",
    "pricing.freeMonthlyOnly": "Monthly only",
    "pricing.perRun": "/ run",
    "pricing.runsIncluded": "runs included",
    "pricing.select": "Select",
    "pricing.lite.name": "Lite",
    "pricing.lite.desc": "Try AI stock picks for free | no strings attached",
    "pricing.free": "FREE",
    "pricing.noCreditCard": "No credit card required",
    "pricing.trialBadge": "Cancel anytime",
    "pricing.trialNote": "Billed upfront | Cancel anytime",
    "pricing.startTrial": "Get Started",
    "pricing.trialNoCharge": "Cancel anytime",
    "checkout.trialNote": "You'll be redirected to secure checkout | Cancel anytime from your profile",
    "faq.trial.q": "Do you offer a money-back guarantee?",
    "faq.trial.a": "Yes. Get a Pro yearly subscription and if your picks don't beat the S&P 500 in the first month, we'll issue a full refund.",
    "pricing.f.3stocks": "3 stock recommendations per run",
    "pricing.x.exclusions": "Stock exclusions",
    "pricing.basic.name": "Basic",
    "pricing.basic.desc": "Quick stock picks for casual investors",
    "pricing.plus.name": "Plus",
    "pricing.plus.desc": "Deeper insights for serious investors",
    "pricing.pro.name": "Pro",
    "pricing.pro.desc": "Maximum intelligence for power investors",
    // Basic features
    "pricing.f.5stocks": "5 stock recommendations",
    "pricing.f.basicRisk": "Basic risk analysis",
    "pricing.f.3m6mROI": "3-month & 6-month ROI",
    "pricing.f.exclusions": "Stock exclusions",
    "pricing.f.news": "Latest news per stock",
    "pricing.f.pdf": "PDF export",
    // Plus features
    "pricing.f.10stocks": "10 stock recommendations per run",
    "pricing.f.detailedRisk": "Detailed risk analysis",
    "pricing.f.roi2yr": "ROI up to 2 years",
    "pricing.f.crypto": "Crypto recommendations",
    "pricing.f.signals": "AI Buy signals with confidence scores",
    "pricing.f.perStockROI": "Per-stock ROI projections",
    "pricing.f.telegramAlerts1": "Up to 1 Telegram alert per week",
    // Pro features
    "pricing.f.15stocks": "15 stock recommendations per run",
    "pricing.f.allROI": "All ROI timeframes (up to 10yr)",
    "pricing.f.confidence": "Confidence scores per stock",
    "pricing.f.riskPlanning": "Personalized risk planning",
    "pricing.f.netWorth": "Net worth & salary analysis",
    "pricing.f.dividend": "Dividend & tax insights",
    "pricing.f.telegramAlerts3": "Up to 3 Telegram alerts per week",
    "pricing.f.dailyDigest": "Daily market digest",
    // Sniper & general alert limits
    "pricing.f.sniperAlerts1": "Sniper Alerts (up to 4/month)",
    "pricing.f.sniperAlerts3": "Sniper Alerts (up to 8/month)",
    "pricing.f.generalAlerts2": "General buying alerts (up to 4/month)",
    "pricing.f.generalAlerts5": "General buying alerts (up to 8/month)",
    // Excludes
    "pricing.x.crypto": "Crypto recommendations",
    "pricing.x.personalAdvice": "Personal financial advice",
    "pricing.everythingInLite": "Everything in Lite AND",
    "pricing.everythingInPlus": "Everything in Plus AND",
    "pricing.custom.title": "Need a Custom Plan?",
    "pricing.custom.desc":
      "For larger run bundles or tailored requirements, contact our support team and we’ll prepare a custom package.",
    "pricing.custom.cta": "Contact Support",

    // Investment Form
    "form.back": "Back to Pricing",
    "form.paymentConfirmed": "Payment Confirmed",
    "form.planPaid": "Plan | paid successfully.",
    "form.freeRunActivated": "Free Run Activated",
    "form.freeRunDesc": "1 free AI signal | no payment required",
    "form.runsRemaining": "remaining",
    "form.title": "Tell us about your investment",
    "form.subtitle": "Our AI will craft personalized stock picks based on your profile.",
    "form.requiredNote": "Fields marked",
    "form.requiredWord": "required",
    "form.mustBeFilled": "must be filled. Everything else is",
    "form.optionalWord": "optional",
    "form.improvesAccuracy": "but improves accuracy.",
    "form.amount": "Investment Amount",
    "form.amountDesc": "Total amount you plan to invest across all recommendations.",
    "form.riskAllocation": "Risk Allocation",
    "form.riskAllocDesc": "Distribute your investment across risk levels. Total must equal 100%.",
    "form.lowRisk": "Low Risk",
    "form.medRisk": "Medium Risk",
    "form.highRisk": "High Risk",
    "form.lowRiskDesc": "Blue chips, bonds, stable dividends",
    "form.medRiskDesc": "Growth stocks, ETFs, balanced funds",
    "form.highRiskDesc": "Speculative, crypto, small caps, high-growth",
    "form.horizon": "Investment Horizon",
    "form.horizonDesc":
      "How long do you plan to hold these investments? This shapes the AI's strategy | shorter horizons favor stable, liquid picks while longer horizons unlock higher-growth opportunities.",
    "form.sectors": "Preferred Sectors",
    "form.sectorsDesc": "Select sectors you're interested in. Leave empty for diversified picks across all sectors.",
    "form.crypto": "Include Cryptocurrencies",
    "form.cryptoDesc": "Add top cryptocurrencies (BTC, ETH, SOL, etc.) to your portfolio mix.",
    "form.cryptoIncluded": "Crypto included ✓",
    "form.cryptoAdd": "Add crypto to recommendations",
    "form.etf": "Include ETFs",
    "form.etfDesc": "Add ETFs (Exchange-Traded Funds) like SPY, QQQ, VTI for diversified exposure.",
    "form.etfIncluded": "ETFs included ✓",
    "form.etfAdd": "Add ETFs to recommendations",
    "form.halal": "Halal only",
    "form.halalDesc": "Restrict recommendations to Sharia-compliant stocks. Excludes conventional banks, alcohol, gambling, tobacco, weapons, pork and adult industries.",
    "form.halalIncluded": "Halal filter on ✓",
    "form.halalOn": "Halal filter on ✓",
    "form.halalOff": "Only show Halal stocks",
    "form.goal": "Investment Goal",
    "form.goalDesc": "What are you primarily trying to achieve with this investment?",
    "form.financialProfile": "Personal Financial Profile",
    "form.financialProfileDesc":
      "Share your financial context so our AI can tailor risk levels and allocation to your real situation.",
    "form.privacyNote":
      "100% anonymous | we never store or share your personal data. It's used only for this single analysis session.",
    "form.netWorth": "Estimated Net Worth",
    "form.netWorthDesc": "Your total assets minus liabilities.",
    "form.salary": "Annual Salary",
    "form.salaryDesc": "Your gross yearly income before taxes.",
    "form.expenses": "Monthly Expenses",
    "form.expensesDesc": "Average total monthly spending including rent, bills, etc.",
    "form.submit": "Generate AI Stock Picks",
    "form.required": "required",
    "form.optional": "optional",
    // Horizons
    "form.h.3months": "3 months",
    "form.h.6months": "6 months",
    "form.h.1year": "1 year",
    "form.h.2years": "2 years",
    "form.h.5years": "5 years",
    "form.h.10years": "10+ years",
    // Sectors
    "form.s.technology": "Technology",
    "form.s.healthcare": "Healthcare",
    "form.s.finance": "Finance",
    "form.s.energy": "Energy",
    "form.s.consumerGoods": "Consumer Goods",
    "form.s.realEstate": "Real Estate",
    "form.s.industrials": "Industrials",
    "form.s.utilities": "Utilities",
    "form.s.aiml": "AI/ML",
    // Goals
    "form.g.wealthBuilding": "Wealth Building",
    "form.g.retirement": "Retirement",
    "form.g.passiveIncome": "Passive Income",
    "form.g.shortTermGains": "Short-term Gains",
    "form.g.diversification": "Diversification",

    // Ticker Search
    "ticker.label": "Stock / Crypto Exclusions",
    "ticker.desc": "Search by name or ticker to exclude from recommendations.",
    "ticker.placeholder": "Search e.g. Tesla, AAPL, Bitcoin...",

    // Results Dashboard
    "results.newAnalysis": "New Analysis",
    "results.exportPdf": "Export PDF",
    "results.langMismatch":
      "This analysis was generated in {lang}. Switch back or run a new analysis to get results in your current language.",
    "results.yourPortfolio": "Your AI Portfolio",
    "results.riskScore": "Risk Score",
    "results.stocks": "Stocks",
    "results.sentiment": "Sentiment",
    "results.bestROI": "Best ROI",
    "results.personalAdvice": "Personalized Financial Advice",
    "results.recommendedRisk": "Recommended Risk Split",
    "results.monthlyInvestment": "Monthly Investment Suggestion",
    "results.emergencyFund": "Emergency Fund",
    "results.retirementReadiness": "Retirement Readiness",
    "results.diversificationAdvice": "Diversification Advice",
    "results.taxConsiderations": "Tax Considerations",
    "results.marketAlerts": "Market Alerts & Upcoming Events",
    "results.projectedROI": "Projected ROI",
    "results.allocation": "Allocation",
    "results.stockRecs": "Stock Recommendations",
    "results.stock": "Stock",
    "results.sector": "Sector",
    "results.risk": "Risk",
    "results.price": "Price",
    "results.signal": "Signal",
    "results.amount": "Amount",
    "results.confidence": "Confidence",
    "results.actions": "Actions",
    "results.aiReasoning": "AI Reasoning",
    "results.projectedROIStock": "Projected ROI for this stock",
    "results.latestNews": "Latest News",
    "results.reallocate": "Reallocate",
    "results.from": "from",
    "results.byRiskLevel": "by risk level:",
    "results.confirmRemoval": "Confirm Removal",
    "results.cancel": "Cancel",
    "results.livePrice": "Live price",
    "results.estimatedPrice": "Estimated price",
    "results.runAgain": "Run Again",
    "results.left": "left",
    "results.purchaseNew": "Purchase New Plan",
    "results.downloadPdf": "Download PDF",
    "results.analyzing": "Analyzing Markets...",
    "results.analyzingDesc": "Our AI is scanning thousands of stocks for your perfect portfolio",
    "results.error": "Something went wrong.",
    "results.tryAgain": "Try Again",
    "results.disclaimer": "⚠️ AI-generated recommendations. Not financial advice. Always do your own research.",
    "results.portfolioValue": "Portfolio Value",
    "results.3moOnly": "(3mo & 6mo only)",
    "results.upTo2yr": "(up to 2yr)",
    "results.removeStock": "Remove stock",

    // Resume Access
    "resume.trigger": "Have unused runs or granted access? Recover here",
    "resume.triggerShort": "Recover access",
    "resume.title": "Resume Access",
    "resume.desc":
      "Enter your private Recovery Code. Paid users receive it after checkout, and granted users receive it from support.",
    "resume.placeholder": "Paste your Recovery Code",
    "resume.cancel": "Cancel",
    "resume.find": "Find My Runs",
    "resume.loading": "Looking up...",
    "resume.noRuns": "No unused runs found for this Recovery Code.",

    // FAQ
    "faq.title": "Frequently Asked Questions",
    "faq.subtitle": "Everything you need to know about Signal GPT",
    "faq.back": "Back",
    "faq.q1": "What is Signal GPT?",
    "faq.a1":
      "Signal GPT is an AI-powered stock analysis tool that generates personalized investment recommendations based on your goals, risk tolerance, and financial profile. We use advanced AI models to analyze thousands of stocks and crypto assets.",
    "faq.q2": "How does the pricing work?",
    "faq.a2":
      "We offer three plans: Lite (Free) includes 1 run per month, Plus ($5.99/mo or $39.99/yr) includes 4 monthly or 6 yearly runs, and Pro ($12.99/mo or $79.99/yr) includes 6 monthly or 10 yearly runs. Each run generates a complete AI portfolio analysis.",
    "faq.q3": "What is a 'run'?",
    "faq.a3":
      "A run is one complete AI stock analysis. You fill out the investment form with your preferences, and the AI generates a full portfolio with stock picks, ROI projections, risk analysis, and news. Each run is a fresh analysis you can customize differently.",
    "faq.q4": "What's the difference between the plans?",
    "faq.a4":
      "Lite gives you 3 stock recommendations per run with 3 and 6 month ROI projections. Plus includes everything in Lite, plus 10 stocks per run, crypto recommendations, AI buy signals with confidence scores, per-stock ROI, and Telegram alerts. Pro includes everything in Plus, with 15 stocks per run, all ROI timeframes up to 10 years, personalized financial advice based on your net worth and salary, and Sniper alerts.",
    "faq.q5": "How do I access my runs across devices?",
    "faq.a5":
      "Your runs are linked to your account. Simply log in on any device to access your remaining runs and full run history.",
    "faq.q6": "Is my financial data stored?",
    "faq.a6":
      "No. Personal financial information like net worth, salary, and expenses is used only during your current analysis session. We never store it permanently or share it with third parties. Payment processing is handled securely by Creem | we never see your card details.",
    "faq.q7": "Is this financial advice?",
    "faq.a7":
      "No. Signal GPT provides AI-generated analysis for informational and educational purposes only. It is not financial, investment, tax, or legal advice. Always consult a qualified financial advisor before making investment decisions.",
    "faq.q8": "What happens if the AI gives wrong recommendations?",
    "faq.a8":
      "AI analysis is based on available market data and models that may contain errors or lag behind real-time conditions. Past performance projections don't guarantee future results. Stock prices and market conditions can change rapidly. We recommend using our analysis as one input alongside your own research.",
    "faq.q9": "Can I exclude specific stocks or crypto?",
    "faq.a9":
      "Yes! All plans include a stock/crypto exclusion feature. You can search by ticker or name to exclude any assets you don't want in your recommendations | useful if you already hold certain positions or want to avoid specific companies.",
    "faq.q10": "Can I export my results?",
    "faq.a10":
      "Yes, all plans include PDF export. You can download a comprehensive report with all your stock picks, ROI projections, risk analysis, news summaries, and (for Pro) personalized financial advice.",
    "faq.q11": "How do I cancel my subscription?",
    "faq.a11":
      "You can cancel your subscription anytime from the receipt email you received when you subscribed. Open the email and follow the cancellation link. If you need help, contact us at signalgpt@protonmail.com.",

    // Index
    "index.runsRemaining": "remaining on your",
    "index.plan": "plan.",
    "index.continue": "Continue",
    "index.run": "run",
    "index.runs": "runs",

    // Checkout
    "checkout.back": "Back to Pricing",
    "checkout.error": "Checkout Error",
    "checkout.redirecting": "Redirecting to checkout...",

    // Payment Success
    "payment.title": "Payment Successful!",
    "payment.desc": "Your subscription is active and your runs are ready to use.",
    "payment.processing": "Processing payment...",
    "payment.continue": "Continue",
    "payment.error": "We couldn't finish setting up your runs. Please contact support.",

    // Footer
    "footer.disclaimer": "⚠️ AI-powered stock analysis tool. Not financial advice. Invest responsibly.",
    "footer.terms": "Terms & Conditions",
    "footer.privacy": "Privacy Policy",
    "footer.contact": "Contact",
    "footer.aiDisclaimer":
      "Signal GPT is an independent product and is not affiliated with Google, OpenAI, Anthropic, or any other AI provider. We access AI models through publicly available APIs.",

    // PDF
    "pdf.title": "Signal GPT Portfolio Report",
    "pdf.plan": "Plan",
    "pdf.generated": "Generated",
    "pdf.riskScore": "Risk Score",
    "pdf.sentiment": "Sentiment",
    "pdf.stocks": "Stocks",
    "pdf.bestRoi": "Best ROI",
    "pdf.personalAdvice": "Personalized Financial Advice",
    "pdf.riskSplit": "Recommended Risk Split",
    "pdf.monthlyInvestment": "Monthly Investment",
    "pdf.emergencyFund": "Emergency Fund",
    "pdf.retirementReadiness": "Retirement Readiness",
    "pdf.diversification": "Diversification",
    "pdf.taxConsiderations": "Tax Considerations",
    "pdf.mo": "mo",
    "pdf.marketAlerts": "Market Alerts & Upcoming Events",
    "pdf.date": "Date",
    "pdf.event": "Event",
    "pdf.impact": "Impact",
    "pdf.description": "Description",
    "pdf.recommendation": "Recommendation",
    "pdf.projectedRoi": "Projected ROI",
    "pdf.period": "Period",
    "pdf.roiPercent": "ROI %",
    "pdf.portfolioValue": "Portfolio Value",
    "pdf.stockRecommendations": "Stock Recommendations",
    "pdf.ticker": "Ticker",
    "pdf.name": "Name",
    "pdf.sector": "Sector",
    "pdf.risk": "Risk",
    "pdf.signal": "Signal",
    "pdf.price": "Price",
    "pdf.allocPercent": "Alloc %",
    "pdf.amount": "Amount",
    "pdf.confidence": "Confidence",
    "pdf.reason": "Reason",
    "pdf.disclaimer": "AI-generated recommendations. Not financial advice. Always do your own research.",

    "notFound.title": "404",
    "notFound.message": "Oops! Page not found",
    "notFound.back": "Return to Home",

    // Terms page
    "terms.back": "Back",
    "terms.title": "Terms & Conditions",
    "terms.lastUpdated": "Last updated: March 25, 2026",

    // News page
    "news.pageTitle": "Market News & Events",
    "news.title": "Market Intelligence",
    "news.subtitle": "AI-curated financial news and upcoming market-moving events, refreshed every 2 hours.",
    "news.metaDesc":
      "Stay ahead with AI-curated financial market news, stock insights, crypto updates, and upcoming events that move markets.",
    "news.marketNews": "Market News",
    "news.upcomingEvents": "Upcoming Events",
    "news.updated": "Updated",
    "news.empty": "News is being generated...",
    "news.emptyDesc": "Our AI is preparing the latest market intelligence. Check back shortly.",
    "news.backHome": "Home",
    "news.ctaTitle": "Ready to Build Your Portfolio?",
    "news.ctaDesc":
      "Use our AI to get personalized stock picks, risk analysis, and ROI forecasts tailored to your goals.",
    "news.ctaButton": "Get Your Stock Picks",
    "footer.news": "Market News",

    // Auth
    "auth.loginTitle": "Welcome Back",
    "auth.loginDesc": "Sign in to access your runs and portfolio analyses.",
    "auth.signupTitle": "Create Account",
    "auth.signupDesc": "Sign up to get started with AI stock analysis.",
    "auth.forgotTitle": "Reset Password",
    "auth.forgotDesc": "Enter your email and we'll send you a reset link.",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.loginBtn": "Sign In",
    "auth.signupBtn": "Create Account",
    "auth.resetBtn": "Send Reset Link",
    "auth.loading": "Please wait...",
    "auth.google": "Continue with Google",
    "auth.apple": "Continue with Apple",
    "auth.or": "or",
    "auth.forgotLink": "Forgot your password?",
    "auth.noAccount": "Don't have an account?",
    "auth.signupLink": "Sign up",
    "auth.hasAccount": "Already have an account?",
    "auth.loginLink": "Sign in",
    "auth.backToLogin": "Back to sign in",
    "auth.backHome": "Back to home",
    "auth.checkEmail": "Check your email to confirm your account.",
    "auth.resetSent": "Password reset link sent to your email.",
    "auth.newPasswordTitle": "Set New Password",
    "auth.newPasswordDesc": "Enter your new password below.",
    "auth.newPassword": "New Password",
    "auth.updatePassword": "Update Password",
    "auth.passwordUpdated": "Password updated successfully!",
    "auth.signOut": "Sign Out",
    "auth.signIn": "Sign In",
    "auth.lostPassword": "Lost your password? Contact support:",
    "auth.qr.title": "Save Your Password",
    "auth.qr.desc": "Screenshot or scan this QR code to save your password. You will need it to sign in again.",
    "auth.qr.copy": "Copy password",
    "auth.qr.warning": "⚠ If you lose your password, your account can only be recovered through support.",
    "auth.qr.support": "Contact us at",
    "auth.qr.saved": "I've Saved My Password | Continue",
  },

  ar: {
    // Hero
    "hero.badge": "ذكاء استثماري مدعوم بالذكاء الاصطناعي",
    "hero.title1": "احصل على أول",
    "hero.title2": "توصية أسهم بالذكاء الاصطناعي مجاناً",
    "hero.subtitle":
      "أخبرنا عن ميزانيتك وأهدافك. احصل على توصيات أسهم بالذكاء الاصطناعي مع عوائد متوقعة في 60 ثانية، مجاناً، بدون بطاقة ائتمان.",
    "hero.cta": "احصل على توصيتك المجانية",
    "hero.viewPricing": "عرض الأسعار",
    "hero.powered": "مدعوم بنماذج ذكاء اصطناعي متقدمة",
    "hero.smartPicks": "اختيارات ذكية",
    "hero.riskAnalysis": "تحليل المخاطر",
    "hero.roiForecast": "توقعات العائد",
    "hero.howItWorksLabel": "كيف يعمل",
    "hero.step1": "أدخل ميزانيتك وأهدافك",
    "hero.step2": "الذكاء الاصطناعي يحلل السوق في الوقت الفعلي",
    "hero.step3": "احصل على اختيارات أسهم مخصصة مع العائد المتوقع",

    // Pricing
    "pricing.title1": "اختر",
    "pricing.title2": "ميزتك",
    "pricing.subtitle": "اختر مرونة شهرية أو وفّر أكثر مع الخطط السنوية. إلغاء في أي وقت.",
    "pricing.mostPopular": "الأكثر شعبية",
    "pricing.monthly": "شهري",
    "pricing.yearly": "سنوي",
    "pricing.save": "وفّر",
    "pricing.month": "شهر",
    "pricing.year": "سنة",
    "pricing.savePercent": "وفّر {percent}% مع السنوي",
    "pricing.runsPerMonth": "تشغيل/شهر",
    "pricing.freeMonthlyOnly": "شهري فقط",
    "pricing.perRun": "/ تشغيل",
    "pricing.runsIncluded": "تشغيلات مضمنة",
    "pricing.select": "اختر",
    "pricing.lite.name": "لايت",
    "pricing.lite.desc": "جرب اختيارات الأسهم بالذكاء الاصطناعي مجاناً | بدون أي التزامات",
    "pricing.free": "مجاني",
    "pricing.noCreditCard": "لا حاجة لبطاقة ائتمان",
    "pricing.trialBadge": "إلغاء في أي وقت",
    "pricing.trialNote": "الدفع مقدماً | يمكنك الإلغاء في أي وقت",
    "pricing.startTrial": "ابدأ الآن",
    "pricing.trialNoCharge": "إلغاء في أي وقت",
    "checkout.trialNote": "ستتم إعادتك إلى صفحة الدفع الآمنة | يمكنك الإلغاء في أي وقت من ملفك الشخصي",
    "faq.trial.q": "هل تقدمون ضمان استرداد الأموال؟",
    "faq.trial.a": "نعم. احصل على اشتراك Pro السنوي، وإذا لم تتفوق توصياتنا على مؤشر S&P 500 خلال الشهر الأول، سنقوم بإعادة المبلغ كاملاً.",
    "pricing.f.3stocks": "3 توصيات أسهم في كل تشغيل",
    "pricing.x.exclusions": "استبعاد الأسهم",
    "pricing.basic.name": "أساسي",
    "pricing.basic.desc": "اختيارات أسهم سريعة للمستثمرين العاديين",
    "pricing.plus.name": "بلس",
    "pricing.plus.desc": "رؤى أعمق للمستثمرين الجادين",
    "pricing.pro.name": "برو",
    "pricing.pro.desc": "ذكاء أقصى للمستثمرين المحترفين",
    "pricing.f.5stocks": "5 توصيات أسهم",
    "pricing.f.basicRisk": "تحليل مخاطر أساسي",
    "pricing.f.3m6mROI": "عائد 3 أشهر و 6 أشهر",
    "pricing.f.exclusions": "استبعاد الأسهم",
    "pricing.f.news": "آخر الأخبار لكل سهم",
    "pricing.f.pdf": "تصدير PDF",
    "pricing.f.10stocks": "10 توصيات أسهم في كل تشغيل",
    "pricing.f.detailedRisk": "تحليل مخاطر مفصل",
    "pricing.f.roi2yr": "عائد حتى سنتين",
    "pricing.f.crypto": "توصيات العملات الرقمية",
    "pricing.f.signals": "إشارات شراء بالذكاء الاصطناعي مع درجات الثقة",
    "pricing.f.perStockROI": "توقعات عائد لكل سهم",
    "pricing.f.telegramAlerts1": "حتى تنبيه تيليجرام واحد في الأسبوع",
    "pricing.f.15stocks": "15 توصية أسهم في كل تشغيل",
    "pricing.f.allROI": "جميع الفترات الزمنية للعائد (حتى 10 سنوات)",
    "pricing.f.confidence": "درجات الثقة لكل سهم",
    "pricing.f.riskPlanning": "تخطيط مخاطر شخصي",
    "pricing.f.netWorth": "تحليل صافي الثروة والراتب",
    "pricing.f.dividend": "رؤى الأرباح والضرائب",
    "pricing.f.telegramAlerts3": "حتى 3 تنبيهات تيليجرام في الأسبوع",
    "pricing.f.dailyDigest": "ملخص السوق اليومي",
    "pricing.f.sniperAlerts1": "تنبيهات القنّاص (حتى 4/شهر)",
    "pricing.f.sniperAlerts3": "تنبيهات القنّاص (حتى 8/شهر)",
    "pricing.f.generalAlerts2": "تنبيهات شراء عامة (حتى 4/شهر)",
    "pricing.f.generalAlerts5": "تنبيهات شراء عامة (حتى 8/شهر)",
    "pricing.x.crypto": "توصيات العملات الرقمية",
    "pricing.x.personalAdvice": "استشارات مالية شخصية",
    "pricing.everythingInLite": "كل ما في لايت وأيضاً",
    "pricing.everythingInPlus": "كل ما في بلس وأيضاً",
    "pricing.custom.title": "هل تحتاج خطة مخصصة؟",
    "pricing.custom.desc": "إذا كنت تحتاج عدد تشغيلات أكبر أو متطلبات خاصة، تواصل مع فريق الدعم وسنجهّز لك باقة مخصصة.",
    "pricing.custom.cta": "تواصل مع الدعم",

    // Investment Form
    "form.back": "العودة للأسعار",
    "form.paymentConfirmed": "تم تأكيد الدفع",
    "form.planPaid": "الخطة | تم الدفع بنجاح.",
    "form.freeRunActivated": "تم تفعيل التشغيل المجاني",
    "form.freeRunDesc": "إشارة ذكاء اصطناعي مجانية واحدة | لا حاجة للدفع",
    "form.runsRemaining": "متبقي",
    "form.title": "أخبرنا عن استثمارك",
    "form.subtitle": "سيقوم ذكاؤنا الاصطناعي بإعداد اختيارات أسهم مخصصة بناءً على ملفك الشخصي.",
    "form.requiredNote": "الحقول المحددة",
    "form.requiredWord": "مطلوبة",
    "form.mustBeFilled": "يجب ملؤها. كل شيء آخر",
    "form.optionalWord": "اختياري",
    "form.improvesAccuracy": "لكنه يحسن الدقة.",
    "form.amount": "مبلغ الاستثمار",
    "form.amountDesc": "المبلغ الإجمالي الذي تخطط لاستثماره عبر جميع التوصيات.",
    "form.riskAllocation": "توزيع المخاطر",
    "form.riskAllocDesc": "وزع استثمارك عبر مستويات المخاطر. يجب أن يساوي المجموع 100%.",
    "form.lowRisk": "مخاطر منخفضة",
    "form.medRisk": "مخاطر متوسطة",
    "form.highRisk": "مخاطر عالية",
    "form.lowRiskDesc": "أسهم ممتازة، سندات، أرباح مستقرة",
    "form.medRiskDesc": "أسهم نمو، صناديق متوازنة",
    "form.highRiskDesc": "مضاربة، عملات رقمية، شركات صغيرة",
    "form.horizon": "أفق الاستثمار",
    "form.horizonDesc": "كم من الوقت تخطط للاحتفاظ بهذه الاستثمارات؟ هذا يشكل استراتيجية الذكاء الاصطناعي.",
    "form.sectors": "القطاعات المفضلة",
    "form.sectorsDesc": "اختر القطاعات التي تهمك. اتركها فارغة للحصول على اختيارات متنوعة.",
    "form.crypto": "تضمين العملات الرقمية",
    "form.cryptoDesc": "أضف أفضل العملات الرقمية (BTC، ETH، SOL، إلخ) إلى محفظتك.",
    "form.cryptoIncluded": "العملات الرقمية مضمنة ✓",
    "form.cryptoAdd": "إضافة العملات الرقمية للتوصيات",
    "form.etf": "تضمين صناديق المؤشرات",
    "form.etfDesc": "أضف صناديق المؤشرات المتداولة مثل SPY و QQQ و VTI للتنويع.",
    "form.etfIncluded": "صناديق المؤشرات مضمنة ✓",
    "form.etfAdd": "إضافة صناديق المؤشرات للتوصيات",
    "form.halal": "حلال فقط",
    "form.halalDesc": "اقتصر التوصيات على الأسهم المتوافقة مع الشريعة. يتم استبعاد البنوك التقليدية والكحول والقمار والتبغ والأسلحة ولحم الخنزير والصناعات للبالغين.",
    "form.halalIncluded": "تصفية الحلال مفعلة ✓",
    "form.halalOn": "تصفية الحلال مفعلة ✓",
    "form.halalOff": "عرض الأسهم الحلال فقط",
    "form.goal": "هدف الاستثمار",
    "form.goalDesc": "ما الذي تحاول تحقيقه بشكل أساسي من هذا الاستثمار؟",
    "form.financialProfile": "الملف المالي الشخصي",
    "form.financialProfileDesc": "شارك سياقك المالي حتى يتمكن ذكاؤنا الاصطناعي من تخصيص مستويات المخاطر.",
    "form.privacyNote": "مجهول 100% | لا نخزن أو نشارك بياناتك الشخصية أبداً. تُستخدم فقط لجلسة التحليل هذه.",
    "form.netWorth": "صافي الثروة المقدر",
    "form.netWorthDesc": "إجمالي أصولك مطروحاً منها الالتزامات.",
    "form.salary": "الراتب السنوي",
    "form.salaryDesc": "دخلك السنوي الإجمالي قبل الضرائب.",
    "form.expenses": "المصاريف الشهرية",
    "form.expensesDesc": "متوسط إجمالي الإنفاق الشهري.",
    "form.submit": "توليد اختيارات الأسهم بالذكاء الاصطناعي",
    "form.required": "مطلوب",
    "form.optional": "اختياري",
    "form.h.3months": "3 أشهر",
    "form.h.6months": "6 أشهر",
    "form.h.1year": "سنة واحدة",
    "form.h.2years": "سنتان",
    "form.h.5years": "5 سنوات",
    "form.h.10years": "+10 سنوات",
    "form.s.technology": "التكنولوجيا",
    "form.s.healthcare": "الرعاية الصحية",
    "form.s.finance": "المالية",
    "form.s.energy": "الطاقة",
    "form.s.consumerGoods": "السلع الاستهلاكية",
    "form.s.realEstate": "العقارات",
    "form.s.industrials": "الصناعات",
    "form.s.utilities": "المرافق",
    "form.s.aiml": "AI/ML",
    "form.g.wealthBuilding": "بناء الثروة",
    "form.g.retirement": "التقاعد",
    "form.g.passiveIncome": "الدخل السلبي",
    "form.g.shortTermGains": "مكاسب قصيرة المدى",
    "form.g.diversification": "التنويع",

    // Ticker Search
    "ticker.label": "استبعاد الأسهم / العملات الرقمية",
    "ticker.desc": "ابحث بالاسم أو الرمز للاستبعاد من التوصيات.",
    "ticker.placeholder": "ابحث مثل Tesla، AAPL، Bitcoin...",

    // Results
    "results.newAnalysis": "تحليل جديد",
    "results.exportPdf": "تصدير PDF",
    "results.langMismatch":
      "تم إنشاء هذا التحليل بـ{lang}. عد إلى اللغة السابقة أو أجرِ تحليلاً جديداً للحصول على النتائج بلغتك الحالية.",
    "results.yourPortfolio": "محفظتك الذكية",
    "results.riskScore": "درجة المخاطر",
    "results.stocks": "الأسهم",
    "results.sentiment": "المزاج",
    "results.bestROI": "أفضل عائد",
    "results.personalAdvice": "نصائح مالية شخصية",
    "results.recommendedRisk": "توزيع المخاطر الموصى به",
    "results.monthlyInvestment": "اقتراح الاستثمار الشهري",
    "results.emergencyFund": "صندوق الطوارئ",
    "results.retirementReadiness": "جاهزية التقاعد",
    "results.diversificationAdvice": "نصائح التنويع",
    "results.taxConsiderations": "اعتبارات ضريبية",
    "results.marketAlerts": "تنبيهات السوق والأحداث القادمة",
    "results.projectedROI": "العائد المتوقع",
    "results.allocation": "التوزيع",
    "results.stockRecs": "توصيات الأسهم",
    "results.stock": "السهم",
    "results.sector": "القطاع",
    "results.risk": "المخاطر",
    "results.price": "السعر",
    "results.signal": "الإشارة",
    "results.amount": "المبلغ",
    "results.confidence": "الثقة",
    "results.actions": "إجراءات",
    "results.aiReasoning": "تحليل الذكاء الاصطناعي",
    "results.projectedROIStock": "العائد المتوقع لهذا السهم",
    "results.latestNews": "آخر الأخبار",
    "results.reallocate": "إعادة توزيع",
    "results.from": "من",
    "results.byRiskLevel": "حسب مستوى المخاطر:",
    "results.confirmRemoval": "تأكيد الإزالة",
    "results.cancel": "إلغاء",
    "results.livePrice": "سعر مباشر",
    "results.estimatedPrice": "سعر تقديري",
    "results.runAgain": "تشغيل مرة أخرى",
    "results.left": "متبقي",
    "results.purchaseNew": "شراء خطة جديدة",
    "results.downloadPdf": "تحميل PDF",
    "results.analyzing": "جاري تحليل الأسواق...",
    "results.analyzingDesc": "ذكاؤنا الاصطناعي يفحص آلاف الأسهم لمحفظتك المثالية",
    "results.error": "حدث خطأ ما.",
    "results.tryAgain": "حاول مرة أخرى",
    "results.disclaimer": "⚠️ توصيات مولدة بالذكاء الاصطناعي. ليست نصيحة مالية. قم دائماً ببحثك الخاص.",
    "results.portfolioValue": "قيمة المحفظة",
    "results.3moOnly": "(3 و 6 أشهر فقط)",
    "results.upTo2yr": "(حتى سنتين)",
    "results.removeStock": "إزالة السهم",

    // Resume
    "resume.trigger": "لديك تشغيلات غير مستخدمة أو وصول ممنوح؟ استرجعها هنا",
    "resume.triggerShort": "استرجاع الوصول",
    "resume.title": "استئناف الوصول",
    "resume.desc":
      "أدخل رمز الاسترجاع الخاص بك. يحصل المشترون عليه بعد الدفع، ويحصل أصحاب الوصول الممنوح عليه من الدعم.",
    "resume.placeholder": "ألصق رمز الاسترجاع",
    "resume.cancel": "إلغاء",
    "resume.find": "ابحث عن تشغيلاتي",
    "resume.loading": "جاري البحث...",
    "resume.noRuns": "لم يتم العثور على تشغيلات غير مستخدمة لهذا الرمز.",

    // FAQ
    "faq.title": "الأسئلة الشائعة",
    "faq.subtitle": "كل ما تحتاج معرفته عن Signal GPT",
    "faq.back": "رجوع",
    "faq.q1": "ما هو Signal GPT؟",
    "faq.a1":
      "Signal GPT هو أداة تحليل أسهم مدعومة بالذكاء الاصطناعي تولد توصيات استثمارية مخصصة بناءً على أهدافك ومستوى المخاطرة وملفك المالي. نستخدم نماذج ذكاء اصطناعي متقدمة لتحليل آلاف الأسهم والعملات الرقمية.",
    "faq.q2": "كيف يعمل نظام الأسعار؟",
    "faq.a2":
      "نقدم ثلاث خطط: لايت (مجاني) يشمل تشغيلاً واحداً شهرياً، بلس ($5.99/شهر أو $39.99/سنة) يشمل 4 تشغيلات شهرية أو 6 سنوية، وبرو ($12.99/شهر أو $79.99/سنة) يشمل 6 تشغيلات شهرية أو 10 سنوية.",
    "faq.q3": "ما هو 'التشغيل'؟",
    "faq.a3":
      "التشغيل هو تحليل أسهم كامل بالذكاء الاصطناعي. تملأ نموذج الاستثمار بتفضيلاتك، ويولد الذكاء الاصطناعي محفظة كاملة مع اختيارات الأسهم وتوقعات العائد وتحليل المخاطر والأخبار.",
    "faq.q4": "ما الفرق بين الخطط؟",
    "faq.a4":
      "لايت يمنحك 3 توصيات أسهم في كل تشغيل مع توقعات عائد لـ 3 و 6 أشهر. بلس يشمل كل ما في لايت، بالإضافة إلى 10 أسهم في كل تشغيل، توصيات العملات الرقمية، إشارات شراء بالذكاء الاصطناعي مع درجات الثقة، وعائد لكل سهم، وتنبيهات تيليجرام. برو يشمل كل ما في بلس، مع 15 سهماً في كل تشغيل، جميع فترات العائد حتى 10 سنوات، نصائح مالية شخصية، وتنبيهات Sniper.",
    "faq.q5": "كيف أصل إلى تشغيلاتي من أجهزة مختلفة؟",
    "faq.a5":
      "تشغيلاتك مرتبطة بحسابك. ببساطة سجّل الدخول من أي جهاز للوصول إلى تشغيلاتك المتبقية وسجل التشغيلات الكامل.",
    "faq.q6": "هل يتم تخزين بياناتي المالية؟",
    "faq.a6":
      "لا. المعلومات المالية الشخصية تُستخدم فقط خلال جلسة التحليل الحالية. لا نخزنها أبداً ولا نشاركها مع أطراف ثالثة. معالجة الدفع تتم بأمان عبر Creem.",
    "faq.q7": "هل هذه نصيحة مالية؟",
    "faq.a7":
      "لا. Signal GPT يقدم تحليلات مولدة بالذكاء الاصطناعي لأغراض تعليمية ومعلوماتية فقط. ليست نصيحة مالية أو استثمارية أو ضريبية أو قانونية.",
    "faq.q8": "ماذا يحدث إذا أعطى الذكاء الاصطناعي توصيات خاطئة؟",
    "faq.a8":
      "تحليل الذكاء الاصطناعي مبني على بيانات السوق المتاحة والنماذج التي قد تحتوي على أخطاء. توقعات الأداء السابق لا تضمن النتائج المستقبلية. نوصي باستخدام تحليلنا كمدخل واحد إلى جانب بحثك الخاص.",
    "faq.q9": "هل يمكنني استبعاد أسهم أو عملات رقمية محددة؟",
    "faq.a9": "نعم! جميع الخطط تشمل ميزة الاستبعاد. يمكنك البحث بالرمز أو الاسم لاستبعاد أي أصول لا تريدها في توصياتك.",
    "faq.q10": "هل يمكنني تصدير نتائجي؟",
    "faq.a10":
      "نعم، جميع الخطط تشمل تصدير PDF. يمكنك تحميل تقرير شامل مع جميع اختيارات الأسهم وتوقعات العائد وتحليل المخاطر.",
    "faq.q11": "كيف ألغي اشتراكي؟",
    "faq.a11":
      "يمكنك إلغاء اشتراكك في أي وقت من البريد الإلكتروني الذي استلمته عند الاشتراك. افتح البريد واتبع رابط الإلغاء. إذا كنت بحاجة إلى مساعدة، تواصل معنا على signalgpt@protonmail.com.",

    // Index
    "index.runsRemaining": "متبقية في خطتك",
    "index.plan": "",
    "index.continue": "متابعة",
    "index.run": "تشغيل",
    "index.runs": "تشغيلات",

    // Checkout
    "checkout.back": "العودة للأسعار",
    "checkout.error": "خطأ في الدفع",
    "checkout.redirecting": "جاري التحويل للدفع...",

    // Payment
    "payment.title": "تم الدفع بنجاح!",
    "payment.desc": "اشتراكك نشط وتشغيلاتك جاهزة للاستخدام.",
    "payment.processing": "جاري معالجة الدفع...",
    "payment.continue": "متابعة",
    "payment.error": "تعذر إكمال إعداد تشغيلاتك. يرجى التواصل مع الدعم.",

    // Footer
    "footer.disclaimer": "⚠️ أداة تحليل أسهم مدعومة بالذكاء الاصطناعي. ليست نصيحة مالية. استثمر بمسؤولية.",
    "footer.terms": "الشروط والأحكام",
    "footer.privacy": "سياسة الخصوصية",
    "footer.contact": "اتصل بنا",
    "footer.aiDisclaimer":
      "Signal GPT هو منتج مستقل وغير تابع لـ Google أو OpenAI أو Anthropic أو أي مزود ذكاء اصطناعي آخر. نصل إلى نماذج الذكاء الاصطناعي عبر واجهات برمجة التطبيقات المتاحة للعامة.",

    // PDF
    "pdf.title": "تقرير محفظة Signal GPT",
    "pdf.plan": "الخطة",
    "pdf.generated": "تاريخ الإنشاء",
    "pdf.riskScore": "درجة المخاطرة",
    "pdf.sentiment": "معنويات السوق",
    "pdf.stocks": "الأسهم",
    "pdf.bestRoi": "أفضل عائد",
    "pdf.personalAdvice": "نصائح مالية شخصية",
    "pdf.riskSplit": "توزيع المخاطر الموصى به",
    "pdf.monthlyInvestment": "الاستثمار الشهري",
    "pdf.emergencyFund": "صندوق الطوارئ",
    "pdf.retirementReadiness": "جاهزية التقاعد",
    "pdf.diversification": "التنويع",
    "pdf.taxConsiderations": "الاعتبارات الضريبية",
    "pdf.mo": "شهر",
    "pdf.marketAlerts": "تنبيهات السوق والأحداث القادمة",
    "pdf.date": "التاريخ",
    "pdf.event": "الحدث",
    "pdf.impact": "التأثير",
    "pdf.description": "الوصف",
    "pdf.recommendation": "التوصية",
    "pdf.projectedRoi": "العائد المتوقع",
    "pdf.period": "الفترة",
    "pdf.roiPercent": "نسبة العائد",
    "pdf.portfolioValue": "قيمة المحفظة",
    "pdf.stockRecommendations": "توصيات الأسهم",
    "pdf.ticker": "الرمز",
    "pdf.name": "الاسم",
    "pdf.sector": "القطاع",
    "pdf.risk": "المخاطرة",
    "pdf.signal": "الإشارة",
    "pdf.price": "السعر",
    "pdf.allocPercent": "النسبة",
    "pdf.amount": "المبلغ",
    "pdf.confidence": "الثقة",
    "pdf.reason": "السبب",
    "pdf.disclaimer": "توصيات مولّدة بالذكاء الاصطناعي. ليست نصيحة مالية. قم دائماً بإجراء بحثك الخاص.",

    // 404
    "notFound.title": "404",
    "notFound.message": "عذراً! الصفحة غير موجودة",
    "notFound.back": "العودة للصفحة الرئيسية",

    // Terms
    "terms.back": "رجوع",
    "terms.title": "الشروط والأحكام",
    "terms.lastUpdated": "آخر تحديث: 25 مارس 2026",

    // News page
    "news.pageTitle": "أخبار السوق والأحداث",
    "news.title": "الاستخبارات السوقية",
    "news.subtitle": "أخبار مالية منسقة بالذكاء الاصطناعي وأحداث قادمة تحرك الأسواق، تُحدّث كل ساعتين.",
    "news.metaDesc":
      "ابقَ في المقدمة مع أخبار الأسواق المالية المنسقة بالذكاء الاصطناعي ورؤى الأسهم والعملات الرقمية والأحداث القادمة.",
    "news.marketNews": "أخبار السوق",
    "news.upcomingEvents": "الأحداث القادمة",
    "news.updated": "تم التحديث",
    "news.empty": "جارٍ إنشاء الأخبار...",
    "news.emptyDesc": "الذكاء الاصطناعي يحضّر أحدث المعلومات السوقية. تحقق قريباً.",
    "news.backHome": "الرئيسية",
    "news.ctaTitle": "هل أنت مستعد لبناء محفظتك؟",
    "news.ctaDesc": "استخدم ذكاءنا الاصطناعي للحصول على توصيات أسهم مخصصة وتحليل مخاطر وتوقعات عوائد مصممة لأهدافك.",
    "news.ctaButton": "احصل على توصيات الأسهم",
    "footer.news": "أخبار السوق",

    // Auth
    "auth.loginTitle": "مرحباً بعودتك",
    "auth.loginDesc": "سجّل الدخول للوصول إلى تشغيلاتك وتحليلات محفظتك.",
    "auth.signupTitle": "إنشاء حساب",
    "auth.signupDesc": "سجّل للبدء في تحليل الأسهم بالذكاء الاصطناعي.",
    "auth.forgotTitle": "استعادة كلمة المرور",
    "auth.forgotDesc": "أدخل بريدك الإلكتروني وسنرسل لك رابط الاستعادة.",
    "auth.email": "البريد الإلكتروني",
    "auth.password": "كلمة المرور",
    "auth.loginBtn": "تسجيل الدخول",
    "auth.signupBtn": "إنشاء حساب",
    "auth.resetBtn": "إرسال رابط الاستعادة",
    "auth.loading": "يرجى الانتظار...",
    "auth.google": "المتابعة مع Google",
    "auth.apple": "المتابعة مع Apple",
    "auth.or": "أو",
    "auth.forgotLink": "نسيت كلمة المرور؟",
    "auth.noAccount": "ليس لديك حساب؟",
    "auth.signupLink": "إنشاء حساب",
    "auth.hasAccount": "لديك حساب بالفعل؟",
    "auth.loginLink": "تسجيل الدخول",
    "auth.backToLogin": "العودة لتسجيل الدخول",
    "auth.backHome": "العودة للرئيسية",
    "auth.checkEmail": "تحقق من بريدك الإلكتروني لتأكيد حسابك.",
    "auth.resetSent": "تم إرسال رابط استعادة كلمة المرور إلى بريدك.",
    "auth.newPasswordTitle": "تعيين كلمة مرور جديدة",
    "auth.newPasswordDesc": "أدخل كلمة المرور الجديدة أدناه.",
    "auth.newPassword": "كلمة المرور الجديدة",
    "auth.updatePassword": "تحديث كلمة المرور",
    "auth.passwordUpdated": "تم تحديث كلمة المرور بنجاح!",
    "auth.signOut": "تسجيل الخروج",
    "auth.signIn": "تسجيل الدخول",
    "auth.lostPassword": "فقدت كلمة المرور؟ تواصل مع الدعم:",
    "auth.qr.title": "احفظ كلمة المرور",
    "auth.qr.desc": "التقط لقطة شاشة أو امسح رمز QR لحفظ كلمة المرور. ستحتاجها لتسجيل الدخول مرة أخرى.",
    "auth.qr.copy": "نسخ كلمة المرور",
    "auth.qr.warning": "⚠ إذا فقدت كلمة المرور، لا يمكن استرداد حسابك إلا عبر الدعم.",
    "auth.qr.support": "تواصل معنا على",
    "auth.qr.saved": "لقد حفظت كلمة المرور | متابعة",
  },

  ru: {
    // Hero
    "hero.badge": "Инвестиционный интеллект на базе ИИ",
    "hero.title1": "Получите первую",
    "hero.title2": "рекомендацию ИИ бесплатно",
    "hero.subtitle":
      "Укажите бюджет и цели. Получите рекомендации акций от ИИ с прогнозом доходности за 60 секунд, бесплатно, без карты.",
    "hero.cta": "Получить бесплатную рекомендацию",
    "hero.viewPricing": "Посмотреть цены",
    "hero.powered": "На базе передовых моделей ИИ",
    "hero.smartPicks": "Умный выбор",
    "hero.riskAnalysis": "Анализ рисков",
    "hero.roiForecast": "Прогноз ROI",
    "hero.howItWorksLabel": "Как это работает",
    "hero.step1": "Введите бюджет и цели",
    "hero.step2": "ИИ анализирует рынок в реальном времени",
    "hero.step3": "Получите персональные рекомендации с прогнозом доходности",

    // Pricing
    "pricing.title1": "Выберите своё",
    "pricing.title2": "преимущество",
    "pricing.subtitle": "Выберите ежемесячную гибкость или сэкономьте с годовыми планами. Отмена в любой момент.",
    "pricing.mostPopular": "САМЫЙ ПОПУЛЯРНЫЙ",
    "pricing.monthly": "Ежемесячно",
    "pricing.yearly": "Ежегодно",
    "pricing.save": "СКИДКА",
    "pricing.month": "мес",
    "pricing.year": "год",
    "pricing.savePercent": "Экономия {percent}% с годовым",
    "pricing.runsPerMonth": "запусков/месяц",
    "pricing.freeMonthlyOnly": "Только ежемесячно",
    "pricing.perRun": "/ запуск",
    "pricing.runsIncluded": "запусков включено",
    "pricing.select": "Выбрать",
    "pricing.lite.name": "Лайт",
    "pricing.lite.desc": "Попробуйте ИИ-рекомендации бесплатно | без обязательств",
    "pricing.free": "БЕСПЛАТНО",
    "pricing.noCreditCard": "Кредитная карта не нужна",
    "pricing.trialBadge": "Отмена в любой момент",
    "pricing.trialNote": "Оплата сразу | Отмена в любой момент",
    "pricing.startTrial": "Начать",
    "pricing.trialNoCharge": "Отмена в любой момент",
    "checkout.trialNote": "Вы будете перенаправлены на безопасную страницу оплаты | Отмена в любой момент в профиле",
    "faq.trial.q": "Есть ли гарантия возврата денег?",
    "faq.trial.a": "Да. Оформите годовую подписку Pro, и если наши рекомендации не превзойдут индекс S&P 500 в первый месяц, мы вернём полную сумму.",
    "pricing.f.3stocks": "3 рекомендации акций за запуск",
    "pricing.x.exclusions": "Исключения акций",
    "pricing.basic.name": "Базовый",
    "pricing.basic.desc": "Быстрые рекомендации для обычных инвесторов",
    "pricing.plus.name": "Плюс",
    "pricing.plus.desc": "Глубокий анализ для серьёзных инвесторов",
    "pricing.pro.name": "Про",
    "pricing.pro.desc": "Максимальный интеллект для профессионалов",
    "pricing.f.5stocks": "5 рекомендаций акций",
    "pricing.f.basicRisk": "Базовый анализ рисков",
    "pricing.f.3m6mROI": "ROI за 3 и 6 месяцев",
    "pricing.f.exclusions": "Исключения акций",
    "pricing.f.news": "Последние новости по каждой акции",
    "pricing.f.pdf": "Экспорт в PDF",
    "pricing.f.10stocks": "10 рекомендаций акций за запуск",
    "pricing.f.detailedRisk": "Детальный анализ рисков",
    "pricing.f.roi2yr": "ROI до 2 лет",
    "pricing.f.crypto": "Рекомендации по криптовалютам",
    "pricing.f.signals": "AI сигналы на покупку с оценкой уверенности",
    "pricing.f.perStockROI": "Прогноз ROI по каждой акции",
    "pricing.f.telegramAlerts1": "До 1 Telegram-оповещения в неделю",
    "pricing.f.15stocks": "15 рекомендаций акций за запуск",
    "pricing.f.allROI": "Все периоды ROI (до 10 лет)",
    "pricing.f.confidence": "Оценка уверенности по каждой акции",
    "pricing.f.riskPlanning": "Персонализированное планирование рисков",
    "pricing.f.netWorth": "Анализ состояния и зарплаты",
    "pricing.f.dividend": "Дивиденды и налоговые инсайты",
    "pricing.f.telegramAlerts3": "До 3 Telegram-оповещений в неделю",
    "pricing.f.dailyDigest": "Ежедневная сводка рынка",
    "pricing.f.sniperAlerts1": "Снайпер-оповещения (до 4/месяц)",
    "pricing.f.sniperAlerts3": "Снайпер-оповещения (до 8/месяц)",
    "pricing.f.generalAlerts2": "Общие оповещения о покупке (до 4/месяц)",
    "pricing.f.generalAlerts5": "Общие оповещения о покупке (до 8/месяц)",
    "pricing.x.crypto": "Рекомендации по криптовалютам",
    "pricing.x.personalAdvice": "Персональные финансовые консультации",
    "pricing.everythingInLite": "Всё из Лайт И",
    "pricing.everythingInPlus": "Всё из Плюс И",
    "pricing.custom.title": "Нужен индивидуальный тариф?",
    "pricing.custom.desc":
      "Если вам нужен больший пакет запусков или особые условия, напишите в поддержку | подготовим персональное предложение.",
    "pricing.custom.cta": "Связаться с поддержкой",

    // Investment Form
    "form.back": "Назад к ценам",
    "form.paymentConfirmed": "Оплата подтверждена",
    "form.planPaid": "План | оплачен успешно.",
    "form.freeRunActivated": "Бесплатный запуск активирован",
    "form.freeRunDesc": "1 бесплатный AI-сигнал | оплата не требуется",
    "form.runsRemaining": "осталось",
    "form.title": "Расскажите о вашем инвестировании",
    "form.subtitle": "Наш ИИ подберёт персонализированные акции на основе вашего профиля.",
    "form.requiredNote": "Поля, отмеченные",
    "form.requiredWord": "обязательно",
    "form.mustBeFilled": "должны быть заполнены. Остальное",
    "form.optionalWord": "необязательно",
    "form.improvesAccuracy": "но повышает точность.",
    "form.amount": "Сумма инвестиций",
    "form.amountDesc": "Общая сумма, которую вы планируете инвестировать.",
    "form.riskAllocation": "Распределение рисков",
    "form.riskAllocDesc": "Распределите инвестиции по уровням риска. Сумма должна быть 100%.",
    "form.lowRisk": "Низкий риск",
    "form.medRisk": "Средний риск",
    "form.highRisk": "Высокий риск",
    "form.lowRiskDesc": "Голубые фишки, облигации, стабильные дивиденды",
    "form.medRiskDesc": "Акции роста, ETF, сбалансированные фонды",
    "form.highRiskDesc": "Спекулятивные, крипто, малые компании",
    "form.horizon": "Горизонт инвестирования",
    "form.horizonDesc": "Как долго вы планируете держать эти инвестиции? Это определяет стратегию ИИ.",
    "form.sectors": "Предпочтительные секторы",
    "form.sectorsDesc": "Выберите интересующие секторы. Оставьте пустым для диверсифицированного выбора.",
    "form.crypto": "Включить криптовалюты",
    "form.cryptoDesc": "Добавить топ криптовалюты (BTC, ETH, SOL и т.д.) в ваш портфель.",
    "form.cryptoIncluded": "Крипто включено ✓",
    "form.cryptoAdd": "Добавить крипто в рекомендации",
    "form.etf": "Включить ETF",
    "form.etfDesc": "Добавить ETF (биржевые фонды) как SPY, QQQ, VTI для диверсификации.",
    "form.etfIncluded": "ETF включены ✓",
    "form.etfAdd": "Добавить ETF в рекомендации",
    "form.halal": "Только халяль",
    "form.halalDesc": "Ограничить рекомендации акциями, соответствующими шариату. Исключаются банки, алкоголь, азартные игры, табак, оружие, свинина и индустрия для взрослых.",
    "form.halalIncluded": "Фильтр халяль включён ✓",
    "form.halalOn": "Фильтр халяль включён ✓",
    "form.halalOff": "Показывать только халяль-акции",
    "form.goal": "Цель инвестирования",
    "form.goalDesc": "Чего вы прежде всего хотите достичь этими инвестициями?",
    "form.financialProfile": "Личный финансовый профиль",
    "form.financialProfileDesc": "Поделитесь финансовым контекстом, чтобы ИИ настроил уровни риска под вашу ситуацию.",
    "form.privacyNote":
      "100% анонимно | мы никогда не храним и не передаём ваши данные. Используется только для этого анализа.",
    "form.netWorth": "Оценочный капитал",
    "form.netWorthDesc": "Общие активы минус обязательства.",
    "form.salary": "Годовой доход",
    "form.salaryDesc": "Ваш годовой доход до налогов.",
    "form.expenses": "Ежемесячные расходы",
    "form.expensesDesc": "Средние ежемесячные расходы включая аренду, счета и т.д.",
    "form.submit": "Сгенерировать рекомендации ИИ",
    "form.required": "обязательно",
    "form.optional": "необязательно",
    "form.h.3months": "3 месяца",
    "form.h.6months": "6 месяцев",
    "form.h.1year": "1 год",
    "form.h.2years": "2 года",
    "form.h.5years": "5 лет",
    "form.h.10years": "10+ лет",
    "form.s.technology": "Технологии",
    "form.s.healthcare": "Здравоохранение",
    "form.s.finance": "Финансы",
    "form.s.energy": "Энергетика",
    "form.s.consumerGoods": "Потребительские товары",
    "form.s.realEstate": "Недвижимость",
    "form.s.industrials": "Промышленность",
    "form.s.utilities": "Коммунальные услуги",
    "form.s.aiml": "AI/ML",
    "form.g.wealthBuilding": "Накопление капитала",
    "form.g.retirement": "Пенсия",
    "form.g.passiveIncome": "Пассивный доход",
    "form.g.shortTermGains": "Краткосрочная прибыль",
    "form.g.diversification": "Диверсификация",

    // Ticker
    "ticker.label": "Исключения акций / крипто",
    "ticker.desc": "Поиск по имени или тикеру для исключения из рекомендаций.",
    "ticker.placeholder": "Поиск: Tesla, AAPL, Bitcoin...",

    // Results
    "results.newAnalysis": "Новый анализ",
    "results.exportPdf": "Экспорт PDF",
    "results.langMismatch":
      "Этот анализ был создан на {lang}. Вернитесь к прежнему языку или запустите новый анализ для получения результатов на текущем языке.",
    "results.yourPortfolio": "Ваш ИИ-портфель",
    "results.riskScore": "Оценка риска",
    "results.stocks": "Акции",
    "results.sentiment": "Настроение",
    "results.bestROI": "Лучший ROI",
    "results.personalAdvice": "Персональные финансовые советы",
    "results.recommendedRisk": "Рекомендуемое распределение рисков",
    "results.monthlyInvestment": "Рекомендация ежемесячных инвестиций",
    "results.emergencyFund": "Резервный фонд",
    "results.retirementReadiness": "Готовность к пенсии",
    "results.diversificationAdvice": "Советы по диверсификации",
    "results.taxConsiderations": "Налоговые аспекты",
    "results.marketAlerts": "Рыночные оповещения и события",
    "results.projectedROI": "Прогнозируемый ROI",
    "results.allocation": "Распределение",
    "results.stockRecs": "Рекомендации по акциям",
    "results.stock": "Акция",
    "results.sector": "Сектор",
    "results.risk": "Риск",
    "results.price": "Цена",
    "results.signal": "Сигнал",
    "results.amount": "Сумма",
    "results.confidence": "Уверенность",
    "results.actions": "Действия",
    "results.aiReasoning": "Анализ ИИ",
    "results.projectedROIStock": "Прогноз ROI для этой акции",
    "results.latestNews": "Последние новости",
    "results.reallocate": "Перераспределить",
    "results.from": "из",
    "results.byRiskLevel": "по уровню риска:",
    "results.confirmRemoval": "Подтвердить удаление",
    "results.cancel": "Отмена",
    "results.livePrice": "Текущая цена",
    "results.estimatedPrice": "Оценочная цена",
    "results.runAgain": "Запустить снова",
    "results.left": "осталось",
    "results.purchaseNew": "Купить новый план",
    "results.downloadPdf": "Скачать PDF",
    "results.analyzing": "Анализируем рынки...",
    "results.analyzingDesc": "Наш ИИ сканирует тысячи акций для вашего идеального портфеля",
    "results.error": "Что-то пошло не так.",
    "results.tryAgain": "Попробовать снова",
    "results.disclaimer":
      "⚠️ Рекомендации сгенерированы ИИ. Не является финансовым советом. Всегда проводите собственное исследование.",
    "results.portfolioValue": "Стоимость портфеля",
    "results.3moOnly": "(только 3 и 6 мес.)",
    "results.upTo2yr": "(до 2 лет)",
    "results.removeStock": "Удалить акцию",

    // Resume
    "resume.trigger": "Есть неиспользованные запуски или предоставленный доступ? Восстановить здесь",
    "resume.triggerShort": "Восстановить доступ",
    "resume.title": "Восстановить доступ",
    "resume.desc":
      "Введите ваш приватный код восстановления. Оплатившие пользователи получают его после оплаты, а пользователи с выданным доступом | от поддержки.",
    "resume.placeholder": "Вставьте код восстановления",
    "resume.cancel": "Отмена",
    "resume.find": "Найти мои запуски",
    "resume.loading": "Ищем...",
    "resume.noRuns": "Неиспользованные запуски для этого кода не найдены.",

    // FAQ
    "faq.title": "Часто задаваемые вопросы",
    "faq.subtitle": "Всё, что нужно знать о Signal GPT",
    "faq.back": "Назад",
    "faq.q1": "Что такое Signal GPT?",
    "faq.a1":
      "Signal GPT | это инструмент анализа акций на базе ИИ, который генерирует персонализированные инвестиционные рекомендации на основе ваших целей, отношения к риску и финансового профиля. Мы используем передовые модели ИИ для анализа тысяч акций и криптоактивов.",
    "faq.q2": "Как работает ценообразование?",
    "faq.a2":
      "У нас три плана: Лайт (бесплатно) включает 1 запуск в месяц, Плюс ($5.99/мес или $39.99/год) включает 4 запуска в месяц или 6 при годовой оплате, Про ($12.99/мес или $79.99/год) включает 6 запусков в месяц или 10 при годовой оплате.",
    "faq.q3": "Что такое 'запуск'?",
    "faq.a3":
      "Запуск | это один полный анализ акций с помощью ИИ. Вы заполняете инвестиционную форму, и ИИ генерирует полный портфель с рекомендациями, прогнозами ROI, анализом рисков и новостями.",
    "faq.q4": "В чём разница между планами?",
    "faq.a4":
      "Лайт | 3 рекомендации акций за запуск с прогнозом ROI на 3 и 6 месяцев. Плюс включает всё из Лайт, плюс 10 акций за запуск, криптовалюты, AI сигналы на покупку с оценкой уверенности, ROI по каждой акции и Telegram-уведомления. Про включает всё из Плюс, с 15 акций за запуск, всеми периодами ROI до 10 лет, персональными финансовыми советами и Sniper-уведомлениями.",
    "faq.q5": "Как получить доступ к запускам с разных устройств?",
    "faq.a5":
      "Ваши запуски привязаны к аккаунту. Просто войдите на любом устройстве, чтобы получить доступ к оставшимся запускам и полной истории.",
    "faq.q6": "Хранятся ли мои финансовые данные?",
    "faq.a6":
      "Нет. Личная финансовая информация используется только во время текущей сессии анализа. Мы никогда не храним и не передаём её третьим лицам. Платежи обрабатываются через Creem.",
    "faq.q7": "Это финансовый совет?",
    "faq.a7":
      "Нет. Signal GPT предоставляет анализ, сгенерированный ИИ, только в информационных и образовательных целях. Это не финансовый, инвестиционный, налоговый или юридический совет.",
    "faq.q8": "Что если ИИ даст неправильные рекомендации?",
    "faq.a8":
      "Анализ ИИ основан на доступных рыночных данных и моделях, которые могут содержать ошибки. Прогнозы прошлых результатов не гарантируют будущих. Мы рекомендуем использовать наш анализ как один из источников наряду с собственным исследованием.",
    "faq.q9": "Могу ли я исключить конкретные акции или крипто?",
    "faq.a9":
      "Да! Все планы включают функцию исключения. Вы можете искать по тикеру или названию, чтобы исключить любые активы из рекомендаций.",
    "faq.q10": "Могу ли я экспортировать результаты?",
    "faq.a10":
      "Да, все планы включают экспорт в PDF. Вы можете скачать подробный отчёт со всеми рекомендациями, прогнозами ROI, анализом рисков и новостями.",
    "faq.q11": "Как отменить подписку?",
    "faq.a11":
      "Вы можете отменить подписку в любое время через письмо-квитанцию, полученное при подписке. Откройте письмо и перейдите по ссылке отмены. Если нужна помощь, свяжитесь с нами по адресу signalgpt@protonmail.com.",

    // Index
    "index.runsRemaining": "осталось в вашем плане",
    "index.plan": "",
    "index.continue": "Продолжить",
    "index.run": "запуск",
    "index.runs": "запусков",

    // Checkout
    "checkout.back": "Назад к ценам",
    "checkout.error": "Ошибка оплаты",
    "checkout.redirecting": "Перенаправление на оплату...",

    // Payment
    "payment.title": "Оплата успешна!",
    "payment.desc": "Ваша подписка активна и запуски готовы к использованию.",
    "payment.processing": "Обработка платежа...",
    "payment.continue": "Продолжить",
    "payment.error": "Не удалось завершить настройку ваших запусков. Пожалуйста, свяжитесь с поддержкой.",

    // Footer
    "footer.disclaimer":
      "⚠️ Инструмент анализа акций на базе ИИ. Не является финансовым советом. Инвестируйте ответственно.",
    "footer.terms": "Условия использования",
    "footer.privacy": "Политика конфиденциальности",
    "footer.contact": "Контакты",
    "footer.aiDisclaimer":
      "Signal GPT является независимым продуктом и не связан с Google, OpenAI, Anthropic или любым другим поставщиком ИИ. Мы получаем доступ к моделям ИИ через общедоступные API.",

    // PDF
    "pdf.title": "Отчёт портфеля Signal GPT",
    "pdf.plan": "План",
    "pdf.generated": "Создан",
    "pdf.riskScore": "Оценка риска",
    "pdf.sentiment": "Настроения рынка",
    "pdf.stocks": "Акции",
    "pdf.bestRoi": "Лучший ROI",
    "pdf.personalAdvice": "Персональные финансовые рекомендации",
    "pdf.riskSplit": "Рекомендуемое распределение рисков",
    "pdf.monthlyInvestment": "Ежемесячные инвестиции",
    "pdf.emergencyFund": "Резервный фонд",
    "pdf.retirementReadiness": "Готовность к пенсии",
    "pdf.diversification": "Диверсификация",
    "pdf.taxConsiderations": "Налоговые аспекты",
    "pdf.mo": "мес",
    "pdf.marketAlerts": "Рыночные оповещения и предстоящие события",
    "pdf.date": "Дата",
    "pdf.event": "Событие",
    "pdf.impact": "Влияние",
    "pdf.description": "Описание",
    "pdf.recommendation": "Рекомендация",
    "pdf.projectedRoi": "Прогнозируемый ROI",
    "pdf.period": "Период",
    "pdf.roiPercent": "ROI %",
    "pdf.portfolioValue": "Стоимость портфеля",
    "pdf.stockRecommendations": "Рекомендации по акциям",
    "pdf.ticker": "Тикер",
    "pdf.name": "Название",
    "pdf.sector": "Сектор",
    "pdf.risk": "Риск",
    "pdf.signal": "Сигнал",
    "pdf.price": "Цена",
    "pdf.allocPercent": "Доля %",
    "pdf.amount": "Сумма",
    "pdf.confidence": "Уверенность",
    "pdf.reason": "Причина",
    "pdf.disclaimer":
      "Рекомендации сгенерированы ИИ. Не является финансовым советом. Всегда проводите собственное исследование.",

    // 404
    "notFound.title": "404",
    "notFound.message": "Упс! Страница не найдена",
    "notFound.back": "Вернуться на главную",

    // Terms
    "terms.back": "Назад",
    "terms.title": "Условия использования",
    "terms.lastUpdated": "Последнее обновление: 25 марта 2026",

    // News page
    "news.pageTitle": "Рыночные новости и события",
    "news.title": "Рыночная аналитика",
    "news.subtitle": "Финансовые новости и предстоящие рыночные события, подобранные ИИ и обновляемые каждые 2 часа.",
    "news.metaDesc":
      "Будьте впереди с ИИ-подобранными новостями финансовых рынков, инсайтами по акциям и криптовалютам и предстоящими событиями.",
    "news.marketNews": "Рыночные новости",
    "news.upcomingEvents": "Предстоящие события",
    "news.updated": "Обновлено",
    "news.empty": "Новости генерируются...",
    "news.emptyDesc": "Наш ИИ подготавливает последнюю рыночную аналитику. Проверьте позже.",
    "news.backHome": "Главная",
    "news.ctaTitle": "Готовы создать свой портфель?",
    "news.ctaDesc":
      "Используйте наш ИИ для персонализированных рекомендаций по акциям, анализа рисков и прогнозов доходности.",
    "news.ctaButton": "Получить рекомендации",
    "footer.news": "Новости рынка",

    // Auth
    "auth.loginTitle": "С возвращением",
    "auth.loginDesc": "Войдите для доступа к вашим запускам и анализам.",
    "auth.signupTitle": "Создать аккаунт",
    "auth.signupDesc": "Зарегистрируйтесь для ИИ-анализа акций.",
    "auth.forgotTitle": "Сброс пароля",
    "auth.forgotDesc": "Введите email, и мы отправим ссылку для сброса.",
    "auth.email": "Email",
    "auth.password": "Пароль",
    "auth.loginBtn": "Войти",
    "auth.signupBtn": "Создать аккаунт",
    "auth.resetBtn": "Отправить ссылку",
    "auth.loading": "Подождите...",
    "auth.google": "Продолжить с Google",
    "auth.apple": "Продолжить с Apple",
    "auth.or": "или",
    "auth.forgotLink": "Забыли пароль?",
    "auth.noAccount": "Нет аккаунта?",
    "auth.signupLink": "Зарегистрироваться",
    "auth.hasAccount": "Уже есть аккаунт?",
    "auth.loginLink": "Войти",
    "auth.backToLogin": "Назад ко входу",
    "auth.backHome": "На главную",
    "auth.checkEmail": "Проверьте email для подтверждения аккаунта.",
    "auth.resetSent": "Ссылка для сброса пароля отправлена.",
    "auth.newPasswordTitle": "Новый пароль",
    "auth.newPasswordDesc": "Введите новый пароль ниже.",
    "auth.newPassword": "Новый пароль",
    "auth.updatePassword": "Обновить пароль",
    "auth.passwordUpdated": "Пароль успешно обновлён!",
    "auth.signOut": "Выйти",
    "auth.signIn": "Войти",
    "auth.lostPassword": "Забыли пароль? Обратитесь в поддержку:",
    "auth.qr.title": "Сохраните пароль",
    "auth.qr.desc": "Сделайте скриншот или отсканируйте QR-код, чтобы сохранить пароль. Он понадобится для входа.",
    "auth.qr.copy": "Копировать пароль",
    "auth.qr.warning": "⚠ Если вы потеряете пароль, восстановить аккаунт можно только через поддержку.",
    "auth.qr.support": "Свяжитесь с нами:",
    "auth.qr.saved": "Я сохранил пароль | Продолжить",
  },
};
